import {Rule, Callback} from "./Rule.ts";
import useStore from "../store.ts";

export class RuleManager {
    private rules: Map<string, Rule>;
    private pendingRules: Map<string, { resolve: () => void; promise: Promise<void> }>;
    /* see docs/EXTRACTED_COMMENTS.md#c781 */
    private missingRules: Set<string>;

    constructor() {
        this.rules = new Map();
        this.pendingRules = new Map();
        this.missingRules = new Set();
    }

    markMissing(name: string): void {
        this.missingRules.add(name);
        const pending = this.pendingRules.get(name);
        if (pending) {
            pending.resolve();
            this.pendingRules.delete(name);
        }
    }

    isMissing(name: string): boolean {
        return this.missingRules.has(name);
    }

    getRules() {
        return this.rules;
    }

    addRule(ruleName: string) {
        const existing = this.rules.get(ruleName);
        if (existing) return existing;
        const rule = new Rule(ruleName);
        this.rules.set(ruleName, rule);
        return rule;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c098 */
    seedFromSnapshot(rules: Record<string, string>): void {
        for (const [name, value] of Object.entries(rules)) {
            const rule = this.getRule(name) ?? this.addRule(name);
            rule.setValue(value);
        }
    }

    getRule(name: string): Rule | undefined {
        return this.rules.get(name);
    }

    listRuleNames(): string[] {
        return [...this.rules.keys()];
    }

    /* see docs/EXTRACTED_COMMENTS.md#c099 */
    syncToServer(): void {
        const store = useStore();
        if (!store.socketManager) return;
        for (const name of this.listRuleNames()) {
            store.socketManager.commandQueue.send("request_rule", {rule_id: name});
        }
    }

    clearRules(): void {
        // see docs/EXTRACTED_COMMENTS.md#c100
        for (const rule of this.rules.values()) {
            rule.clearCallbacks();
        }
        this.rules.clear();
        this.pendingRules.forEach(({resolve}) => resolve());
        this.pendingRules.clear();
        this.missingRules.clear();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c101 */
    removeCallbacksForOwner(ownerId: string): number {
        if (!ownerId) return 0;
        let total = 0;
        for (const rule of this.rules.values()) {
            total += rule.removeCallbacksForOwner(ownerId);
        }
        return total;
    }

    requestRuleWithPromise(ruleName: string): Promise<void> {
        const existingRule = this.getRule(ruleName);
        if (existingRule?.getValue() || this.isMissing(ruleName)) {
            return Promise.resolve();
        }

        const existingPending = this.pendingRules.get(ruleName);
        if (existingPending) {
            return existingPending.promise;
        }

        if (!existingRule) {
            this.addRule(ruleName);
        }

        let resolve!: () => void;
        const promise = new Promise<void>((r) => {
            resolve = r;
        });
        this.pendingRules.set(ruleName, {resolve, promise});
        return promise;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c102 */
    handleRuleUpdate(name: string, ruleValue?: string, hash?: string): void {
        // see docs/EXTRACTED_COMMENTS.md#c103
        const resolvePending = () => {
            const pending = this.pendingRules.get(name);
            if (pending) {
                pending.resolve();
                this.pendingRules.delete(name);
            }
        };

        if (!ruleValue) {
            resolvePending();
            return;
        }
        const rule = this.getRule(name) ?? this.addRule(name);
        this.missingRules.delete(name);

        rule.setValue(ruleValue, hash);
        const store = useStore();
        rule.getCallbacks().forEach((c: Callback) => {
            const ownerId = c.getOwnerId();
            if (ownerId != null && !store.components[ownerId]) return;
            c.getCallback()(rule, c.getRow(), c.getChannel());
        });

        resolvePending();
    }
}